import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ConstareAIPanel,
  ConstareAIPanelProvider,
} from '../components/ConstareAIPanel';

/**
 * Lesson 7.3 visual preview at /preview/lesson/7.3
 * "The teammate who refuses to use AI"
 * Carve-out: how to work with a teammate who will not touch AI, without
 * hiding the fact that you do, and without making them feel left behind.
 */

export default function LessonPreviewPage7_3() {
  return (
    <ConstareAIPanelProvider>
      <LessonPage />
    </ConstareAIPanelProvider>
  );
}

const SECTION_IDS = [
  'sec-cold-open',
  'sec-from-last-time',
  'sec-teaching',
  'sec-your-turn',
  'sec-quick-check',
  'sec-lock-card',
];

function LessonPage() {
  const totalSections = 6;
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const vis = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (vis[0]) {
          const idx = SECTION_IDS.indexOf(vis[0].target.id);
          if (idx >= 0) setActiveIndex(idx);
        }
      },
      { rootMargin: '-30% 0px -55% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    SECTION_IDS.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const progressPct = useMemo(
    () => Math.round(((activeIndex + 1) / totalSections) * 100),
    [activeIndex]
  );

  return (
    <div className="relative -mx-6 -my-10 min-h-screen bg-ink">
      <div className="sticky top-0 z-20 border-b border-line bg-ink/95 backdrop-blur">
        <div className="mx-auto flex max-w-content items-center gap-6 px-6 py-3">
          <Link to="/dashboard" className="text-xs text-muted hover:text-text">
            ← Home
          </Link>
          <div className="hidden flex-1 sm:block">
            <div className="flex items-center gap-3">
              <div className="h-1 flex-1 overflow-hidden rounded-full bg-elevated">
                <div
                  className="h-full bg-amber transition-all duration-300"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted">
                {activeIndex + 1} / {totalSections}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto grid max-w-content gap-8 px-6 py-10 lg:grid-cols-[200px_minmax(0,1fr)_380px]">
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <SectionNav activeIndex={activeIndex} />
          </div>
        </aside>
        <article className="min-w-0">
          <LessonHeader />
          <ColdOpen />
          <FromPrev />
          <Teaching />
          <YourTurn />
          <QuickCheck />
          <LockCard />
          <NextUp />
        </article>
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <ConstareAIPanel />
          </div>
        </aside>
      </div>
    </div>
  );
}

function LessonHeader() {
  return (
    <header className="mb-12">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber">
        Week 7 · Working with people who use AI differently · Lesson 7.3
      </p>
      <h1 className="mt-3 text-4xl font-semibold leading-[1.1] tracking-tight text-text">
        The teammate who refuses to use AI
      </h1>
      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted">
        <span className="flex items-center gap-1.5">
          <ClockIcon /> 40 min
        </span>
        <span className="text-line">|</span>
        <span>Threads: Carve-out · Respect · Honesty</span>
      </div>
      <p className="mt-3 text-xs font-mono uppercase tracking-wider text-amber">
        3 carve-out drills
      </p>
    </header>
  );
}

/* --- Section 1: Cold open --- */
function ColdOpen() {
  return (
    <Section id="sec-cold-open" kicker="Cold open" title="She is the best writer on the team. She will not touch AI. The deadline is in three days.">
      <p className="mt-6 text-text/90">
        Senior year capstone project. Four-person team. The strongest writer
        in the group, by far, is the one who keeps saying she will not use AI.
        Her position is not loud. It is steady.
      </p>
      <p className="mt-4 text-text/90">
        Two of you on the team draft sections with AI, edit them by hand,
        and ship. The fourth uses AI heavily and ships fast. You can feel
        the team starting to route work around her: she writes slower than
        you draft. Her sections are better, but they take twice as long.
      </p>
      <p className="mt-4 text-text/90">
        In a meeting last week, the AI-heavy teammate joked that she should
        &ldquo;just use ChatGPT like the rest of us.&rdquo; She smiled and
        said nothing. You watched her quietly shrink.
      </p>

      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <div className="rounded-lg border border-line bg-surface p-5">
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
            The wrong move 01
          </p>
          <p className="mt-3 text-text/90">
            Route around her. Give her smaller pieces. Let her fall off the
            project quietly. The deadline gets met. The team finishes weaker
            and she finishes worse.
          </p>
        </div>
        <div className="rounded-lg border border-line bg-surface p-5">
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
            The wrong move 02
          </p>
          <p className="mt-3 text-text/90">
            Pretend you are not using AI either. Edit the AI draft until it
            looks like raw writing. Hide your workflow from her. Build a lie
            into the team that will leak the moment she finds out.
          </p>
        </div>
      </div>

      <p className="mt-6 text-text/90">
        Both moves fail. Lesson 7.3 is the third move: carve out room for both workflows without
        hiding what you do.
      </p>
    </Section>
  );
}

/* --- Section 2: From 7.2 --- */
function FromPrev() {
  return (
    <Section id="sec-from-last-time" kicker="From last time · Lesson 7.2" title="7.2 was the teammate who over-trusts AI. 7.3 is the opposite.">
      <p className="mt-6 text-text/90">
        In 7.2 the human override protected the work from someone who was using AI
        too loosely. In 7.3 the problem flips. The teammate is being more
        careful than you are. She is also being slower, and the gap is real,
        and the team is reacting to it whether anyone says so.
      </p>
      <p className="mt-4 text-text/90">
        The mistake here is to treat refusal as a problem to be solved.
        Refusal is a position. Usually a reasoned one. Your job is not to
        convert her. It is to build a way of working that lets her stay her
        and the team still ship.
      </p>
      <ol className="mt-6 space-y-4">
        <ReviewItem n="01" q="Define Human override from 7.2 in one sentence." />
        <ReviewItem n="02" q="Why hiding your AI use from a refuser is a worse move than disagreeing with her." />
        <ReviewItem n="03" q="Today&apos;s posture in one phrase: carve out, do not ___." />
      </ol>
    </Section>
  );
}

function ReviewItem({ n, q }: { n: string; q: string }) {
  return (
    <li className="flex gap-4">
      <span className="font-mono text-sm text-amber pt-1">{n}.</span>
      <span className="text-text/90" dangerouslySetInnerHTML={{ __html: q }} />
    </li>
  );
}

/* --- Section 3: Teaching --- */
function Teaching() {
  return (
    <Section id="sec-teaching" kicker="Teaching · The carve-out" title="A refuser is not a problem. They are a position. Carve-outs respect positions; they do not flatten them.">
      <Subhead n={1} title="The vocab for the lesson" />
      <VocabCard
        term="Carve-out"
        definition="A scoped section of team work where a teammate&rsquo;s no-AI position is respected fully. A carve-out names which parts of the work are theirs (no AI touches them, from anyone), which parts you can draft with AI, and how the handoff between the two works. It is honest about your own AI use, respects their reasoning without arguing it away, and does not require either of you to change how you work."
        analogy="A carve-out in a contract is a specific area the general rule does not apply to. Same idea here. The team is using AI. Your teammate is not. The carve-out names exactly which parts of the work follow her rule and which parts follow the team&rsquo;s, so nobody has to argue every task."
        memoryHook="A carve-out names two lanes; it does not merge them. If your move requires the refuser to start using AI, or you to start hiding it, you have not carved anything out."
        why="Refusers are often the most thoughtful people on a team. They have usually been burned, taught carefully, or read deeply about the costs. If you push them out, you lose the teammate most likely to catch the things AI gets wrong. If you hide from them, you lose their trust permanently the first time they find out. The carve-out is what keeps them on the team without either of you pretending."
      />

      <Subhead n={2} title="The four pillars of a real carve-out" className="mt-12" />
      <p className="mt-4 text-text/90">
        Each pillar is something you do, not something they do. Carve-outs are
        built from your side, because you are the one who can build them
        without compromising what they care about.
      </p>
      <PillarsGrid />

      <Subhead n={3} title="What refusal usually means (and what it does not)" className="mt-12" />
      <p className="mt-4 text-text/90">
        Refusal is not one thing. It is at least four. The right carve-out for
        a teammate depends on which one is underneath.
      </p>
      <div className="mt-6 rounded-lg border border-amber/40 bg-amber/[0.04] p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-amber">
          Four reasons people refuse AI
        </p>
        <ol className="mt-3 space-y-3 text-text/90">
          <li><span className="font-mono text-xs text-amber mr-2">01.</span> A class or a teacher told them it counts as cheating. The rule is real to them. Respect the rule, plan around it.</li>
          <li><span className="font-mono text-xs text-amber mr-2">02.</span> They tried it, got burned (false citation, made-up quote, a job loss in their network), and decided the risk is not worth the speed.</li>
          <li><span className="font-mono text-xs text-amber mr-2">03.</span> They believe the craft itself matters. Writing is thinking. Outsourcing the writing outsources the thinking. This is a value, not a misunderstanding.</li>
          <li><span className="font-mono text-xs text-amber mr-2">04.</span> Quiet ethical concerns about the models, the data, the labor, or the energy. They will rarely lead with it. They are not wrong to hold it.</li>
        </ol>
      </div>

      <Subhead n={4} title="Where AI helps and where AI hurts on carving out" className="mt-12" />
      <p className="mt-4 text-text/90">
        AI is useful for preparing yourself to have the conversation. It is
        worse than useless if it shows up in the conversation itself,
        because the whole point is meeting the teammate where they are.
      </p>
      <SplitGrid />

      <Subhead n={5} title="The honesty rule" className="mt-12" />
      <p className="mt-4 text-text/90">
        The single move that breaks a carve-out faster than anything is hiding
        your AI use from a refuser. Once they find out (and they always do),
        you have not only used the tool they object to, you have lied about
        it. Two violations for the price of one.
      </p>
      <div className="mt-6 rounded-lg border border-emerald-500/40 bg-emerald-500/[0.04] p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-300">
          The rule, in one line
        </p>
        <p className="mt-3 text-text/90">
          Tell the refuser exactly what you used AI for, before they ask.
          &ldquo;I drafted this with AI and edited it by hand&rdquo; is a
          carve-out. &ldquo;I wrote this myself&rdquo; with an AI-drafted
          document is not.
        </p>
      </div>
    </Section>
  );
}

const PILLARS = [
  {
    n: '01',
    name: 'Be honest about your workflow',
    body: 'Tell the refuser what you used AI for, in plain language, before they ask. “I drafted the structure with AI, then rewrote it from scratch” is a sentence that builds trust. Hidden workflows kill it.',
  },
  {
    n: '02',
    name: 'Respect the reason without arguing with it',
    body: 'You do not have to agree with the refuser\'s reasoning to take it seriously. “That makes sense, I get why you draw the line there” is enough. You are not in the room to debate AI ethics. You are in the room to ship.',
  },
  {
    n: '03',
    name: 'Divide work by what each of you is best at',
    body: 'A refuser who is a strong writer is a gift. Give her the sections where craft matters most. Take the sections where speed and volume matter more. Both contributions count. Neither has to be hidden.',
  },
  {
    n: '04',
    name: 'Never use AI on the refuser\'s pieces without asking',
    body: 'If she is the lead on a section, your AI does not touch that section. Not for editing, not for “tightening,” not for ideas. If you genuinely think AI could help, ask first, and accept “no” without resentment.',
  },
];

function PillarsGrid() {
  return (
    <div className="mt-6 space-y-4">
      {PILLARS.map((p) => (
        <div key={p.n} className="rounded-lg border border-amber/40 bg-amber/[0.04] p-5">
          <div className="flex flex-wrap items-baseline gap-3">
            <span className="font-mono text-xs text-muted">Pillar {p.n}</span>
            <h4 className="text-lg font-semibold text-amber" dangerouslySetInnerHTML={{ __html: p.name }} />
          </div>
          <p
            className="mt-3 text-text/90"
            dangerouslySetInnerHTML={{ __html: p.body }}
          />
        </div>
      ))}
    </div>
  );
}

const SPLIT = [
  {
    side: 'AI helps',
    items: [
      'Mapping out what each teammate actually does best, including the refuser.',
      'Drafting a clear workflow statement you can share with the team about how you use AI.',
      'Generating questions you could ask the refuser to understand where their line is.',
      'Catching yourself if you are about to use AI on a section that is not yours.',
    ],
    accent: 'emerald' as const,
  },
  {
    side: 'AI hurts',
    items: [
      'Editing the refuser\'s writing without asking. Even small edits count.',
      'Rewriting her sentences to match the team\'s pace. That erases her voice.',
      'Arguing the refuser into using AI. You are not on the team to convert anyone.',
      'Hiding the AI step from her. The carve-out collapses the moment she finds out.',
    ],
    accent: 'rose' as const,
  },
];

function SplitGrid() {
  return (
    <div className="mt-6 grid gap-4 lg:grid-cols-2">
      {SPLIT.map((col) => {
        const isEm = col.accent === 'emerald';
        const borderCls = isEm ? 'border-emerald-500/40' : 'border-rose-500/40';
        const bgCls = isEm ? 'bg-emerald-500/[0.04]' : 'bg-rose-500/[0.04]';
        const labelCls = isEm ? 'text-emerald-300' : 'text-rose-300';
        return (
          <div key={col.side} className={`rounded-lg border ${borderCls} ${bgCls} p-5`}>
            <p className={`font-mono text-[11px] uppercase tracking-wider ${labelCls}`}>
              {col.side}
            </p>
            <ul className="mt-3 space-y-2">
              {col.items.map((it, i) => (
                <li key={i} className="flex gap-2 text-sm text-text/90">
                  <span className={`shrink-0 ${labelCls}`}>·</span>
                  <span dangerouslySetInnerHTML={{ __html: it }} />
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

/* --- Section 4: Your turn --- */
function YourTurn() {
  return (
    <Section id="sec-your-turn" kicker="Your turn · Pick the move that carves out room for both" title="Three scenes. Two responses. Pick the one that keeps the refuser on the team without hiding your AI use.">
      <p className="mt-6 text-text/90">
        Each scene has a teammate who has drawn a clear line on AI. Both
        responses might keep the project moving. Only one is a real carve-out.
        Pick the move that respects the line and tells the truth.
      </p>
      <div className="mt-8 space-y-6">
        {DRILLS.map((d) => (
          <BridgeDrill key={d.n} drill={d} />
        ))}
      </div>
    </Section>
  );
}

type DrillSpec = {
  n: string;
  scene: string;
  optionA: string;
  optionB: string;
  correct: 'A' | 'B';
  verdict: string;
};

const DRILLS: DrillSpec[] = [
  {
    n: '01',
    scene:
      'Senior capstone team of four. Strongest writer in the group has said she does not use AI. The literature review is due Friday. She is assigned the lit-review draft. By Wednesday night, she has six paragraphs. You have used AI to draft your section in two hours and yours is twice the length.',
    optionA:
      'Quietly run her six paragraphs through AI overnight to “tighten” them, then send the polished version back to her as if you only made minor edits. Project moves forward.',
    optionB:
      'Message her: “Want to split the lit review? You take the framing and the close, I take the middle three sources. I\'ll draft mine with AI and clean it up, you write yours your way. Sound good?”',
    correct: 'B',
    verdict:
      'Option B keeps her in, plays to her strengths, names your workflow openly, and gives her sections she is best suited for. It does not require her to change anything she believes. Option A is the single worst move: it edits her work using the tool she has refused, hides the edit, and treats her position as something to work around rather than work with. When she finds out (she will), the team is done.',
  },
  {
    n: '02',
    scene:
      'First job out of college, a small marketing team. A senior copywriter tells you privately he does not use AI for any client-facing copy, because of a bad experience with hallucinated case studies at his last firm. Your team lead just told the team to draft this week\'s campaign with AI first to move faster.',
    optionA:
      'Tell the team lead privately: “I\'ll draft mine with AI, but I\'d like to give the senior pieces to [name] without that step. He has reasons I respect. We can compare the outputs.”',
    optionB:
      'Send him an AI draft anyway, framed as a “starting point.” If he asks, downplay it: “Mostly my own writing, AI just helped with a couple of phrases.”',
    correct: 'A',
    verdict:
      'Option A respects what he told you in confidence, makes the case to your lead in a way that does not throw him under the bus, and offers a way to compare results without forcing him to compromise his line. Option B drags him into the AI workflow under false framing, betrays the confidence he gave you, and turns the next disagreement into a much harder conversation.',
  },
  {
    n: '03',
    scene:
      'Volunteer team writing a grant proposal for a youth nonprofit. A board member who used to teach writing says flatly: “The proposal narrative needs to be written by a human. Funders can tell.” The team is split. The deadline is in five days. You think AI could help on the budget narrative section specifically.',
    optionA:
      'Say in the next team meeting: “Totally hear you on the program narrative. Can I draft the budget narrative section with AI as a starting point, then hand it to you for the final pass? You catch anything that does not sound right, and the program narrative stays fully yours.”',
    optionB:
      'Use AI to draft the entire proposal anyway and not bring it up, since “everyone uses it now.” The board member is a volunteer; she will not read it that closely.',
    correct: 'A',
    verdict:
      'Option A does the carve-out cleanly: names the section, names who has final say (she does), and respects the part she cares most about (the program narrative). The carve-out is one section wide, not the whole proposal. Option B is the classic refuser-friction failure: assume the position is naive, assume she will not check, push past her line. That is how trust on volunteer boards dies in one round.'
  },
];

function BridgeDrill({ drill }: { drill: DrillSpec }) {
  const [picked, setPicked] = useState<'A' | 'B' | null>(null);
  const wasWrong = picked !== null && picked !== drill.correct;

  return (
    <div className="rounded-lg border border-line bg-surface p-5">
      <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
        Drill {drill.n} · The scene
      </p>
      <p
        className="mt-3 text-text/90"
        dangerouslySetInnerHTML={{ __html: drill.scene }}
      />

      <div className="mt-5 border-t border-line pt-4">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
          Which move is the real carve-out?
        </p>
        <div className="mt-3 grid gap-3 lg:grid-cols-2">
          {(['A', 'B'] as const).map((key) => {
            const text = key === 'A' ? drill.optionA : drill.optionB;
            const isPicked = picked === key;
            const isCorrect = key === drill.correct;
            const shouldShowCorrect = picked !== null && isCorrect;
            const shouldShowWrong = isPicked && !isCorrect;
            let cls =
              'rounded-md border border-line bg-elevated p-4 text-left text-sm text-text/85 hover:border-amber transition-colors';
            if (shouldShowCorrect)
              cls =
                'rounded-md border border-emerald-500 bg-emerald-500/[0.08] p-4 text-left text-sm text-text';
            if (shouldShowWrong)
              cls =
                'rounded-md border border-rose-500 bg-rose-500/[0.08] p-4 text-left text-sm text-text';
            return (
              <button
                key={key}
                className={cls}
                onClick={() => setPicked(key)}
              >
                <span className="font-mono text-[10px] uppercase tracking-wider text-muted block">
                  Option {key}
                </span>
                <span
                  className="mt-2 block"
                  dangerouslySetInnerHTML={{ __html: text }}
                />
              </button>
            );
          })}
        </div>

        {wasWrong && (
          <div className="mt-4 rounded-md border border-rose-500/40 bg-rose-500/[0.06] p-4">
            <p className="font-mono text-[11px] uppercase tracking-wider text-rose-300">
              Not this one · Option {picked}
            </p>
            <p className="mt-2 text-sm text-text/85">
              That move either hides your AI use or forces the refuser past
              her line. Carve-outs only work when both sides stay where they
              are.
            </p>
          </div>
        )}

        {picked !== null && (
          <div className="mt-4 rounded-md border border-emerald-500/40 bg-emerald-500/[0.04] p-4">
            <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-300">
              {wasWrong ? 'The real carve-out' : 'Correct'} · Option {drill.correct}
            </p>
            <p className="mt-2 text-sm text-text/90">{drill.verdict}</p>
          </div>
        )}
      </div>
    </div>
  );
}

/* --- Section 5: Quick check --- */
function QuickCheck() {
  return (
    <Section id="sec-quick-check" kicker="Quick check" title="Five questions. Answer first. Then reveal.">
      <div className="mt-6 space-y-4">
        <QuickCheckItem n="Q1" q="Define Carve-out in one sentence." answer="A scoped section of team work where a teammate&rsquo;s no-AI position is respected fully: it names which parts of the work are theirs, which parts you can draft with AI, and how the handoff works." />
        <QuickCheckItem n="Q2" q="The four pillars of a real carve-out." answer="Be honest about your workflow. Respect the reason without arguing with it. Divide work by strengths. Never use AI on the refuser&apos;s sections without asking." />
        <QuickCheckItem n="Q3" q="The four reasons people refuse AI." answer="A class or rule that defined it as cheating. A bad experience that taught them the risk. A belief that the craft (writing as thinking) matters. Ethical concerns about the models, data, labor, or energy." />
        <QuickCheckItem n="Q4" q="Two things AI helps with on carving out. Two things AI hurts." answer="Helps: mapping team strengths, drafting your workflow statement, generating questions for the refuser, catching yourself before you touch their sections. Hurts: editing their writing, rewriting their voice, arguing them into AI, hiding the AI step." />
        <QuickCheckItem n="Q5" q="The honesty rule, in one line." answer="Tell the refuser exactly what you used AI for, before they ask. Hidden workflows break the carve-out the moment they are discovered, and they always are." />
      </div>
    </Section>
  );
}

/* --- Section 6: Lock card --- */
function LockCard() {
  return (
    <Section id="sec-lock-card" kicker="Lock card" title="Screenshot this. The carve-out map.">
      <div className="mt-6 rounded-2xl border border-amber/40 bg-gradient-to-b from-amber/[0.06] to-transparent p-7" data-lockcard-body>
        <div className="flex items-start justify-between">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-wider text-amber">
              Lesson 7.3 · Lock card · The teammate who refuses to use AI
            </p>
          </div>
          <button className="font-mono text-[10px] uppercase tracking-wider text-muted hover:text-text">
            ↓ Save image
          </button>
        </div>
        <h3 className="mt-4 text-2xl font-semibold leading-snug text-text">
          Refusal is a position, not a problem. A carve-out respects both
          positions; it does not flatten either one.
        </h3>

        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          <LockTile n="01" name="Be honest" body="Name your AI workflow plainly. Hidden workflows kill a carve-out the moment they leak." />
          <LockTile n="02" name="Respect the reason" body='"That makes sense, I get why you draw the line there." You do not have to agree.' />
          <LockTile n="03" name="Divide by strengths" body="Refusers who write well get the craft sections. You take the volume sections. Both count." />
          <LockTile n="04" name="Hands off" body="No AI on the refuser\'s pieces without asking. Not even editing. Not even tightening." />
        </div>

        <div className="mt-6 rounded-md border border-line bg-surface p-4">
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
            What refusal often means
          </p>
          <p className="mt-2 text-text/90">
            A class rule that defined it as cheating. A bad experience that
            taught them the risk. A belief that the craft is the thinking.
            Ethical concerns about the models. Usually some mix.
          </p>
        </div>

        <div className="mt-4 rounded-md border border-emerald-500/40 bg-emerald-500/[0.04] p-4">
          <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-300">
            The honesty rule
          </p>
          <p className="mt-2 text-text/90">
            Tell the refuser what you used AI for, before they ask. “I
            drafted with AI and edited by hand” is a carve-out. “I
            wrote this myself” with an AI draft underneath is not.
          </p>
        </div>

        <p className="mt-6 text-sm text-muted italic">
          Carve-outs are built from your side. Refusers do not owe you the work
          of building one.
        </p>
      </div>
    </Section>
  );
}

function LockTile({ n, name, body }: { n: string; name: string; body: string }) {
  return (
    <div className="rounded-lg border border-line bg-surface p-4">
      <div className="flex items-baseline gap-2">
        <span className="font-mono text-[10px] text-muted">{n}</span>
        <span className="rounded bg-amber/15 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-amber">
          {name}
        </span>
      </div>
      <p
        className="mt-3 text-sm text-text/85"
        dangerouslySetInnerHTML={{ __html: body }}
      />
    </div>
  );
}

/* --- Next up --- */
function NextUp() {
  return (
    <div className="mt-14 rounded-xl border border-line bg-surface p-6" data-nextup-card>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-xl">
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
            Up next · Lesson 7.4
          </p>
          <h3 className="mt-2 text-xl font-semibold text-text">
            Disagreeing about AI without burning the room
          </h3>
          <p className="mt-3 text-sm text-text/85">
            Vocab: Frame. How to disagree with a teammate about AI use in a
            way that produces a decision instead of a fight, and the three
            framings that turn an argument into a working conversation.
          </p>
        </div>
        <button className="rounded-md border border-amber/40 px-4 py-2 text-sm font-medium text-amber hover:bg-amber/[0.08]">
          Coming soon
        </button>
      </div>
    </div>
  );
}

/* --- Primitives --- */

function Section({
  id,
  kicker,
  title,
  children,
}: {
  id: string;
  kicker: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="mt-16 scroll-mt-24">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber">
        {kicker}
      </p>
      <h2 className="mt-3 text-2xl font-semibold leading-snug text-text">{title}</h2>
      <div>{children}</div>
    </section>
  );
}

function Subhead({ n, title, className = '' }: { n: number; title: string; className?: string }) {
  return (
    <h3 className={`text-xl font-semibold text-text ${className}`}>
      <span className="mr-3 font-mono text-sm text-amber">{n}</span>
      {title}
    </h3>
  );
}

function VocabCard({
  term,
  definition,
  analogy,
  memoryHook,
  why,
}: {
  term: string;
  definition: string;
  analogy: string;
  memoryHook: string;
  why: string;
}) {
  const [revealed, setRevealed] = useState(false);
  return (
    <div className="mt-6 rounded-xl border border-amber/40 bg-amber/[0.04] p-6">
      <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
        Vocab · 7.3 · New term
      </p>
      <h4 className="mt-2 text-2xl font-semibold text-text">{term}</h4>
      {!revealed ? (
        <div className="mt-5">
          <p className="text-sm text-text/85">
            Write your own one-sentence definition first. Then reveal.
          </p>
          <textarea
            className="mt-3 w-full rounded-md border border-line bg-elevated p-3 text-sm text-text placeholder:text-muted focus:border-amber focus:outline-none"
            rows={3}
            placeholder={`Your definition of "${term}"...`}
          />
          <button
            className="mt-3 rounded-md bg-amber px-4 py-2 text-sm font-medium text-ink hover:opacity-90"
            onClick={() => setRevealed(true)}
          >
            Reveal definition →
          </button>
        </div>
      ) : (
        <div className="mt-5 space-y-4">
          <VocabBlock label="Definition" body={definition} />
          <VocabBlock label="Analogy" body={analogy} accent />
          <VocabBlock label="Memory hook" body={memoryHook} />
          <VocabBlock label="Why it matters" body={why} />
        </div>
      )}
    </div>
  );
}

function VocabBlock({ label, body, accent = false }: { label: string; body: string; accent?: boolean }) {
  return (
    <div
      className={`rounded-md border ${accent ? 'border-amber/40 bg-amber/[0.04]' : 'border-line bg-surface'} p-4`}
    >
      <p className={`font-mono text-[10px] uppercase tracking-wider ${accent ? 'text-amber' : 'text-muted'}`}>
        {label}
      </p>
      <p className="mt-2 text-sm text-text/90">{body}</p>
    </div>
  );
}

function QuickCheckItem({ n, q, answer }: { n: string; q: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-line bg-surface p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex gap-3">
          <span className="font-mono text-xs text-amber pt-0.5">{n}</span>
          <p className="text-text/90" dangerouslySetInnerHTML={{ __html: q }} />
        </div>
        <button
          onClick={() => setOpen(!open)}
          className="shrink-0 rounded-md border border-line px-3 py-1 text-xs text-muted hover:border-amber hover:text-text"
        >
          {open ? 'Hide' : 'Reveal'}
        </button>
      </div>
      {open && (
        <p
          className="mt-3 border-t border-line pt-3 text-sm text-text/85"
          dangerouslySetInnerHTML={{ __html: answer }}
        />
      )}
    </div>
  );
}

function SectionNav({ activeIndex }: { activeIndex: number }) {
  const items = [
    'Cold open',
    'From 7.2',
    'The carve-out',
    'Your turn',
    'Quick check',
    'Lock card',
  ];
  return (
    <nav>
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
        In this lesson
      </p>
      <ol className="mt-4 space-y-1">
        {items.map((label, i) => {
          const isActive = i === activeIndex;
          const isPast = i < activeIndex;
          return (
            <li key={label}>
              <a
                href={`#${SECTION_IDS[i]}`}
                className={`flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? 'bg-elevated text-text'
                    : 'text-muted hover:text-text'
                }`}
              >
                <span
                  className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-mono ${
                    isActive
                      ? 'bg-amber text-ink'
                      : isPast
                      ? 'border border-amber/60 text-amber'
                      : 'border border-line text-muted'
                  }`}
                >
                  {isPast ? '✓' : i + 1}
                </span>
                <span>{label}</span>
              </a>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function ClockIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-muted">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}
